import { useState } from "react";
import { Link } from "react-router-dom";
import { useOpenApprovals } from "@api/hooks/useApprovalRequests";
import { DocumentsPager } from "@components/Review/DocumentsPager";
import { Button } from "@components/common/Button";
import type { OpenApprovalRequest } from "@schemas/api";

const labels = {
  invoice: "Invoice",
  receipt: "Receipt",
  purchase_order: "Purchase order",
  ap_posting: "AP posting",
};
const actionLabel = (action: string | null) =>
  action ? action.replace(/_/g, " ") : "Review";

export function OpenApprovalList({
  recordType,
}: {
  recordType?: OpenApprovalRequest["record_type"];
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const query = useOpenApprovals((page - 1) * pageSize, pageSize, recordType);
  return (
    <section aria-label="Open approval requests" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-content-primary">
            Open approval requests
          </h2>
          <p className="text-sm text-content-secondary">
            Pending, held and escalated requests you can access, oldest first.
            Each row is one approval request.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
        >
          Refresh
        </Button>
      </div>
      {query.isLoading ? (
        <p role="status">Loading open approval requests…</p>
      ) : query.error ? (
        <p role="alert">Could not load approval requests. Please retry.</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-border-default bg-surface-base">
            <table
              className="min-w-full text-left text-sm"
              aria-label="Open approval requests"
            >
              <thead className="bg-surface-raised text-content-secondary">
                <tr>
                  {[
                    "Type",
                    "Request",
                    "Requested by",
                    "Awaiting",
                    "Status",
                    "Opened",
                    "Review",
                  ].map((label) => (
                    <th
                      key={label}
                      scope="col"
                      className="px-4 py-3 font-medium"
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border-default">
                {query.data?.items.map((request) => (
                  <tr key={request.id}>
                    <td className="whitespace-nowrap px-4 py-4">
                      {labels[request.record_type]}
                    </td>
                    <td className="px-4 py-4">
                      <span className="block font-medium text-content-primary">
                        {request.title}
                      </span>
                      <span className="block text-xs capitalize text-content-secondary">
                        {actionLabel(request.action_type)}
                      </span>
                    </td>
                    <td className="px-4 py-4">{request.requested_by || "—"}</td>
                    <td className="px-4 py-4">
                      <span className="block">
                        {request.approvers.join(", ") || "Approver assignment"}
                      </span>
                      <span className="text-xs text-content-secondary">
                        Step {request.current_step}
                        {request.total_steps
                          ? ` of ${request.total_steps}`
                          : ""}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className="rounded-full bg-amber-50 px-2 py-1 text-xs capitalize text-amber-900">
                        {request.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-4">
                      <time dateTime={request.requested_at}>
                        {new Date(request.requested_at).toLocaleString()}
                      </time>
                    </td>
                    <td className="px-4 py-4">
                      {request.review_url ? (
                        <Link
                          to={request.review_url}
                          className="font-medium text-primary-600 underline"
                          aria-label={`Review ${labels[request.record_type].toLowerCase()}: ${request.title}`}
                        >
                          Review
                        </Link>
                      ) : (
                        <span className="text-content-secondary">
                          Unavailable
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!query.data?.items.length && (
              <p
                role="status"
                className="p-8 text-center text-content-secondary"
              >
                No open approval requests.
              </p>
            )}
          </div>
          <DocumentsPager
            total={query.data?.total ?? 0}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            idPrefix="open-approvals"
          />
        </>
      )}
    </section>
  );
}
